import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Lock, Mail, AlertCircle, Loader2 } from "lucide-react";

function fmtError(d) {
  if (d == null) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d.msg || d);
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      const dest = loc.state?.from?.pathname || "/admin";
      nav(dest, { replace: true });
    } catch (e) {
      setErr(fmtError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4 relative overflow-hidden">
      {/* Subtle barley-coloured glow */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 25% 15%, #f5a623 0%, transparent 45%), radial-gradient(circle at 80% 85%, #c41e3a 0%, transparent 45%)" }} />
      {/* Top + bottom red Wilson stripes */}
      <div className="absolute top-0 inset-x-0 h-[3px] bg-[#c41e3a]" />
      <div className="absolute bottom-0 inset-x-0 h-[3px] bg-[#c41e3a]" />

      <div className="relative w-full max-w-md">
        {/* Wilson logo */}
        <div className="flex items-center justify-center mb-8">
          <img src="/wilson-logo.png" alt="Wilson Contractors" className="h-16 w-auto" />
        </div>

        <form onSubmit={submit}
              className="bg-white border border-slate-200 rounded-2xl p-7 shadow-xl shadow-slate-200/60">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#c41e3a] font-bold mb-1">Staff sign-in</div>
          <h1 className="font-display font-black text-2xl text-[#1a1a5c] mb-1">Welcome back</h1>
          <p className="text-xs text-slate-500 mb-6">Authorised personnel only — Wilson Contractors admin console.</p>

          {err && (
            <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-lg px-3 py-2 mb-4" data-testid="login-error">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <label className="block mb-3">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Email</span>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="email" required autoFocus value={email} onChange={e => setEmail(e.target.value)}
                     data-testid="login-email"
                     className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1a1a5c] focus:bg-white transition-colors"
                     placeholder="you@wilson-contractors.co.uk" />
            </div>
          </label>

          <label className="block mb-5">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Password</span>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                     data-testid="login-password"
                     className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1a1a5c] focus:bg-white transition-colors"
                     placeholder="••••••••" />
            </div>
          </label>

          <button type="submit" disabled={busy} data-testid="login-submit"
                  className="w-full bg-[#1a1a5c] hover:bg-[#11113d] disabled:opacity-60 text-white font-bold rounded-lg py-2.5 text-sm flex items-center justify-center gap-2 transition-colors shadow-lg shadow-slate-300">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : "Sign in"}
          </button>

          <p className="text-[11px] text-slate-400 text-center mt-5">
            Need help signing in? Speak to the office.
          </p>
        </form>

        <p className="text-center text-[10px] uppercase tracking-[0.3em] text-slate-400 mt-6">
          Wilson Contractors · Estate office
        </p>
      </div>
    </div>
  );
}
